import {
    useCallback, useEffect, useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { getClientId, isQRIban } from 'utils';

import { validateIban } from 'utils/validation';
import { UseWithdrawalPlanReturn } from 'widgets/WithdrawalPlan/types';
import { useWithdrawalPlanBase } from 'widgets/WithdrawalPlan/hooks/useWithdrawalPlanBase';
import { useThreeBProductType } from 'hooks/isThreeBProduct';
import { useProductsList } from 'hooks/useProducts';
import { roundToNearestTenth } from 'utils/formatting';

import HandlerError from '../../../errors/HandlerError';
import handlerRequestCanceling from '../../../utils/handlerRequestCanceling';
import {
    useCreatePortfolioSelectors,
} from '../../../datasource/CreatePortfolio';

interface UseShoppingCartWithdrawalPlanReturn extends UseWithdrawalPlanReturn {
    onContinue: (data?: any) => void;
}

export const useWithdrawalPlanChangePlan = ({
    portfolio, withdrawalOption, paymentInstruction, onChangeStrategy,
}): UseShoppingCartWithdrawalPlanReturn => {
    const { dfsClientId } = useParams();
    const contactId = getClientId(dfsClientId);

    const { products } = useProductsList(contactId);

    const selectedProduct = products?.find(
        ({ id }) => portfolio?.existingProduct?.id === id,
    );

    const productSettings = useMemo(
        () => selectedProduct?.settings,
        [selectedProduct],
    );

    const {
        currentClientSelector,
    } = useCreatePortfolioSelectors();

    const {
        isAzp, isZiv, isZifd, isPk, isAzpI, isAzpD,
    } = useThreeBProductType({ product: portfolio?.existingProduct });

    const {
        data,
        setData,
        withdrawalPlanData,
        planCalculationLoading,
        errorData,
        trigger,
        setErrorData,
        isRegularTransferPlan,
        setIsRegularTransferPlan,
        memberId,
        option,
        isAtLeastOneProductOpened,
        setLoading,
        isLoading,
        optionWithdrawalPeriodicity,
        isOptionsLoading,
        calculateWithdrawalPlan,
        displayWithdrawalPlanCalculations,
        disableDateHandler,
        disableDateHandlerAzp,
        setPlanCalculationLoading,
        control,
        handleSubmit,
        register,
    } = useWithdrawalPlanBase({
        isAzp,
        isPk,
        isZiv,
        isZifd,
        isAzpD,
        productSettings,
        modelPortfolioId: portfolio?.modelPortfolioId?.toString() || '',
        strategyId: portfolio?.investmentApplication?.investmentDescription?.selectStrategy?.strategyId?.toString() || '',
        investmentApplicationId: portfolio?.investmentApplication?.investmentApplicationId?.toString(),
        setPageErrors: () => undefined,
    });

    useEffect(() => {
        const firstName = currentClientSelector?.firstName || '';
        const lastName = currentClientSelector?.lastName || '';

        const adaptedData = {
            amount: withdrawalOption?.withdrawalAmount?.toFixed(2),
            frequency: withdrawalOption?.periodicity,
            targetPortfolio: withdrawalOption?.targetPortfolio?.toString(),
            startDate: moment(withdrawalOption?.startDate).format('MM/YYYY'),
            withdrawalsStartDate: withdrawalOption?.startDate,
            iban: paymentInstruction?.iban ?? '',
            esr: paymentInstruction?.esr ?? '',
            firstName: paymentInstruction?.firstName ?? firstName ?? '',
            lastName: paymentInstruction?.lastName ?? lastName ?? '',
            bankName: paymentInstruction?.bankName ?? '',
            zipCode: paymentInstruction?.zipCity ?? '',
            city: paymentInstruction?.city ?? '',
            duration: withdrawalOption?.duration,
        };

        setData(adaptedData);
    }, [currentClientSelector?.firstName, currentClientSelector?.lastName, isAzp, withdrawalOption, paymentInstruction]);

    const onContinue = useCallback(
        async (dta) => {
            try {
                const ibanError = await validateIban(dta?.iban);

                if (ibanError) {
                    setErrorData('iban', {
                        message: ibanError as string,
                    });

                    return;
                }

                const paymentInstructions:any = {
                    iban: dta?.iban,
                    firstName: dta?.firstName,
                    lastName: dta?.lastName,
                    bankName: dta?.bankName,
                    zipCity: dta?.zipCode,
                    city: dta?.city,
                };

                if (dta?.esr && isQRIban(dta?.iban)) {
                    paymentInstructions.esr = dta.esr;
                }

                const withdrawalData = {
                    withdrawalOption: true,
                    withdrawal: {
                        withdrawalAmount: roundToNearestTenth(dta?.amount),
                        periodicity: dta?.frequency,
                        startDate: moment(data?.startDate, 'MM/YYYY').format(moment.defaultFormatUtc),
                        investmentAmount: roundToNearestTenth(dta?.amount),
                    },
                    paymentInstruction: paymentInstructions,
                };

                onChangeStrategy({ withdrawalPlanData: withdrawalData });
            } catch (err: any) {
                handlerRequestCanceling(
                    HandlerError({
                        setError: () => {},
                        setLoading: setLoading || (() => {}),
                    }),
                )(err);
            }
        },
        [
            contactId,
            memberId,
            isAzp,
            withdrawalPlanData,
            isRegularTransferPlan,
            portfolio?.investmentApplication?.investmentApplicationId,
            onChangeStrategy,
        ],
    );

    return {
        data,
        withdrawalPlanData,
        planCalculationLoading,
        errorData,
        clearErrors: () => {},
        isRegularTransferPlan,
        optionWithdrawalPeriodicity,
        isLoading,
        isOptionsLoading,
        productExternalId: portfolio?.existingProduct?.externalId,
        productDesc: portfolio?.investmentApplication?.investmentDescription,
        isAzp,
        isPk,
        isZiv,
        isZifd,
        isAzpI,
        isAzpD,
        calculateWithdrawalPlan,
        displayWithdrawalPlanCalculations,
        disableDateHandler,
        disableDateHandlerAzp,
        setData,
        setErrorData,
        trigger,
        option,
        isAtLeastOneProductOpened,
        setIsRegularTransferPlan,
        setPlanCalculationLoading,
        control,
        register,
        onContinue: handleSubmit ? handleSubmit(onContinue) : () => {},
        withdrawalOption: portfolio?.withdrawalOptionFromExistingApplication,
    };
};
